import { useState } from 'react';

import { Box, Button, Table } from '@zillow/constellation-10';

import { AccountViewListingGroupType, PropertyType } from '@zg-rentals/ts-rental-manager-api-client';

import { useGetMultiplexUnits } from '../../../hooks/queries/properties/zrm';
import { useScreenSize } from '../../../hooks/useScreenSize';
import { multiplexSkeletonPlaceholderListing, transformFiltersToApiParams } from '../../../utils/propertiesPageUtils';

import PropertiesConstants from '../../../constants/PropertiesConstants';

import PropertyRow from '../PropertyRow';

import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';
import type {
  AccountViewListingGroupDto,
  AccountViewMultiplexUnitDto,
  WebAnalyticsLeaseDto,
} from '@zg-rentals/ts-rental-manager-api-client';

type PropertyHeaderRowProps = {
  listing: AccountViewListingGroupDto;
  onUpsellCellHover: (
    columnLabel: string,
    currentProperty: ZrmClickstreamListing,
    lease?: WebAnalyticsLeaseDto,
  ) => void;
  isScrolledToLeft: boolean;
  filters: { searchText: string; subNavFilterType: string };
};

const MULTIPLEX_UNIT_SKELETON_ROW_COUNT = 3;

const mapUnitToListingGroupRow = (unit: AccountViewMultiplexUnitDto): AccountViewListingGroupDto => {
  return {
    type: AccountViewListingGroupType.LISTING,
    groupId: unit.groupId,
    listingAlias: unit.listingAlias,
    name: unit.name,
    photoKey: unit.photoKey,
    propertyType: PropertyType.UNKNOWN,
    status: unit.status,
    isMultifamily: false,
    address: unit.address,
    listingCount: {
      active: 0,
      archived: 0,
      atRisk: 0,
      freeBonusListingCount: 0,
      freeClaimedListingCount: 0,
      freeListingsOwnedByCustomer: 0,
      inactive: 0,
      total: 0,
    },
  };
};

const PropertyHeaderRow = ({ listing, onUpsellCellHover, isScrolledToLeft, filters }: PropertyHeaderRowProps) => {
  const { isScreenLgGTE } = useScreenSize();
  const [isExpanded, setIsExpanded] = useState(false);

  const apiParams = {
    ...transformFiltersToApiParams(filters),
    limit: PropertiesConstants.PROPERTIES_MULTIPLEX_LIMIT_DEFAULT,
  };
  const groupId = listing.groupId;

  const {
    dataByGroupId,
    fetchNextPageForGroup,
    hasNextPageByGroupId,
    isFetchingByGroupId,
    isFetchingNextPageByGroupId,
  } = useGetMultiplexUnits(apiParams, isExpanded ? [groupId] : []);

  const unitsData = dataByGroupId[groupId];
  const units = unitsData?.units || [];
  const hasNextPage = hasNextPageByGroupId[groupId] || false;
  const isFetching = isFetchingByGroupId[groupId] || false;
  const isFetchingNextPage = isFetchingNextPageByGroupId[groupId] || false;

  const showMultiplexUnitSkeletons = isFetching && units.length === 0;

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  const handleLoadMore = () => {
    const endKey = unitsData?.pager?.endKey;

    if (endKey && fetchNextPageForGroup) {
      fetchNextPageForGroup(groupId, endKey);
    }
  };

  return (
    <>
      {/* parent row */}
      <PropertyRow
        data-testid="PropertyHeaderRow"
        index={0}
        listing={listing}
        isLoading={false}
        filters={filters}
        onUpsellCellHover={onUpsellCellHover}
        isScrolledToLeft={isScrolledToLeft}
        onExpandGroupClick={handleExpandClick}
        isRowOpen={isExpanded}
        isFetching={isFetching}
      />

      {/* skeleton rows for loading state */}
      {isExpanded &&
        showMultiplexUnitSkeletons &&
        Array.from({ length: MULTIPLEX_UNIT_SKELETON_ROW_COUNT }, (_, i) => (
          <PropertyRow
            key={`multiplex-unit-skeleton-${groupId}-${i}`}
            index={i}
            listing={multiplexSkeletonPlaceholderListing(groupId)}
            filters={filters}
            isLoading
            onUpsellCellHover={onUpsellCellHover}
            isScrolledToLeft={isScrolledToLeft}
          />
        ))}

      {/* unit rows */}
      {isExpanded &&
        !showMultiplexUnitSkeletons &&
        units.map((unit, i) => (
          <PropertyRow
            key={`unit-${groupId}-${unit.listingAlias}`}
            index={i}
            listing={mapUnitToListingGroupRow(unit)}
            filters={filters}
            onUpsellCellHover={onUpsellCellHover}
            isScrolledToLeft={isScrolledToLeft}
          />
        ))}

      {/* load more units button */}
      {isExpanded && hasNextPage && (
        <Table.Row
          key={`loadMore-${groupId}`}
          data-testid="PropertyHeaderRow-LoadMoreUnitsRow"
          css={{ backgroundColor: '{colors.bg.softest}' }}
        >
          <Table.Cell
            colSpan={isScreenLgGTE ? 5 : 2}
            css={{
              padding: '400',
              textAlign: 'center',
            }}
          >
            <Box css={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <Button tone="brand" emphasis="outlined" size="sm" onClick={handleLoadMore} loading={isFetchingNextPage}>
                Load more units
              </Button>
            </Box>
          </Table.Cell>
        </Table.Row>
      )}
    </>
  );
};

export { PropertyHeaderRow };
export type { PropertyHeaderRowProps };
export default PropertyHeaderRow;
